import { useEffect } from 'react';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import type { User } from 'oidc-client-ts';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.reactRouterBrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: false,
        blockAllMedia: false,
      }),
    ],
    replaysSessionSampleRate: 1,
    replaysOnErrorSampleRate: 1,
  });
}

const getProfileString = (value: unknown): string | undefined => {
  if (typeof value !== 'string' || !value.trim()) {
    return undefined;
  }

  return value.trim();
};

export const syncSentryUser = ({ user }: { user: User | null }): void => {
  if (!SENTRY_DSN) {
    return;
  }

  if (!user?.profile) {
    Sentry.setUser(null);
    return;
  }

  const { sub, email, name, preferred_username } = user.profile;

  Sentry.setUser({
    id: getProfileString(sub),
    email: getProfileString(email),
    username: getProfileString(preferred_username) ?? getProfileString(name) ?? getProfileString(email),
  });
};
